import { isUserData } from "common/Database";
import { LoadingIndicator } from "designer/apps/orgs/orgs-styles";
import * as React from "react";
import { useSelector } from "react-redux";
import { RootState } from "store/types";
import InputLabel from "designer/ui/smart/input-label/input-label";
import { GlideDropdown } from "designer/ui/smart/glide-dropdown/glide-dropdown";
import { OrgIcon } from "designer/apps/orgs/orgs-sidebar/org-icon";
import { isValidEmailAddress } from "common/Support";
import { saveUserUpdates } from "lib/firebase-functions";
import { Button } from "designer/ui/smart/button/button-style";
import { trackEvent } from "analytics";
import { OnboardingInfo, reasonForGlideKey, ReasonForGlideKey, reasonsForGlide } from "./onboardingInfo";

interface Props {
    orgID: string;
    onDone(isForWork: boolean): void;
    updateOnboardingInfo(updates: Partial<OnboardingInfo>): void;
}

function isFirstNameValid(name: string): boolean {
    return name.trim() !== "" && !isValidEmailAddress(name);
}

const CreateTeamNameAndUse: React.FunctionComponent<Props> = ({ orgID, onDone, updateOnboardingInfo }) => {
    const userData = useSelector((state: RootState) => state.userData);
    const orgs = useSelector((state: RootState) => state.organizations);
    const [reasonForGlide, setReasonForGlide] = React.useState<ReasonForGlideKey>("Select");
    const [firstName, setFirstName] = React.useState("");
    const [lastName, setLastName] = React.useState("");
    const [submitting, setSubmitting] = React.useState(false);

    const validFirstName = isFirstNameValid(firstName);
    const isForWork: boolean = ["For work", "For my company"].includes(reasonForGlide);
    const newName: string = [firstName, lastName].join(" ").trim();
    const valid: boolean = validFirstName && reasonForGlide !== "Select";

    React.useEffect(() => {
        if (isUserData(userData) && userData.displayName !== undefined && userData.displayName !== "") {
            const [f, ...l] = userData.displayName.trim().includes(" ")
                ? userData.displayName.split(" ")
                : [userData.displayName, ""];

            if (firstName === "" && isFirstNameValid(f)) {
                setFirstName(f);
            }

            if (lastName === "") {
                setLastName(l.join(" ").trim());
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onSubmit = React.useCallback(async () => {
        const reason = reasonsForGlide[reasonForGlide];
        if (newName !== "" && reason !== undefined) {
            const updates: Partial<OnboardingInfo> = {
                reasonForGlide: reason,
                displayName: newName,
            };
            setSubmitting(true);
            trackEvent("sign up user survey", { intent: reason });
            await saveUserUpdates(updates, {});
            setSubmitting(false);

            updateOnboardingInfo({
                ...updates,
                isForWork,
            });

            onDone(isForWork);
        }
    }, [reasonForGlide, newName, isForWork, updateOnboardingInfo, onDone]);

    if (!isUserData(userData)) {
        return <LoadingIndicator />;
    }

    const org = orgs.find(o => o.id === orgID);

    return (
        <>
            <hgroup>
                <OrgIcon size={"xlarge"} label={org?.displayName || ""} />
                <h1>{validFirstName ? `Welcome, ${firstName}` : `Welcome To Glide!`}</h1>
            </hgroup>

            <div className="two-fields">
                <div className="field">
                    <label>First Name</label>
                    <InputLabel
                        margin={8}
                        autoFocus={true}
                        focusBorder={true}
                        label={undefined}
                        placeholder={"first name"}
                        debounceUpdate={true}
                        value={firstName}
                        onChange={e => setFirstName(e.target.value)}
                        tallerVariation={true}
                    />
                </div>

                <div className="field">
                    <label>Last Name</label>
                    <InputLabel
                        margin={8}
                        autoFocus={false}
                        focusBorder={true}
                        label={undefined}
                        placeholder={"last name"}
                        debounceUpdate={true}
                        value={lastName}
                        onChange={e => setLastName(e.target.value)}
                        tallerVariation={true}
                    />
                </div>
            </div>

            <div className="field">
                <label>How will you use Glide?</label>
                <GlideDropdown<ReasonForGlideKey>
                    items={reasonForGlideKey}
                    descriptionForItem={i => i}
                    selected={reasonForGlide}
                    onItemSelect={setReasonForGlide}
                    tallerVariation={true}
                />
            </div>
            <div className="continue">
                <Button variant="filled" disabled={!valid || submitting} onClick={onSubmit} tallerVariation={true}>
                    {submitting ? `Saving your information...` : `Continue`}
                </Button>
            </div>
        </>
    );
};

export default CreateTeamNameAndUse;
